import { useCallback, useEffect, useState } from 'react';
import { useApiContext } from '../../../api';
import { OrganizationViewStateMetadata, ViewStateAdminQueryParams } from '../../../api/models/apis';
import { useAppSelector } from '../../../app/hooks';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import styles from './OrganizationViewStates.module.scss';
import { ViewStateCard } from './ViewStateCard';
import { getAssetTypeName } from '../../../data/assets/helpers';
import { Spinner } from '../../../components/primitives/Spinner';
import { debounceInComponent } from '../../../components/miscUtils';
import { VIEW_STATE_TERMS } from '../../../data/snapshot/dashboardStateSerialization';

const VIEW_STATE_SORT_OPTIONS = [
  { key: 'name_asc', value: 'name', ascValue: true, label: 'Name (A-Z)' },
  { key: 'name_desc', value: 'name', ascValue: false, label: 'Name (Z-A)' },
  { key: 'asset_display_name_asc', value: 'asset_display_name', ascValue: true, label: 'Asset name (A-Z)' },
  { key: 'asset_display_name_desc', value: 'asset_display_name', ascValue: false, label: 'Asset name (Z-A)' },
  { key: 'created_at_asc', value: 'created_at', ascValue: true, label: 'Created (Old - New)' },
  { key: 'created_at_desc', value: 'created_at', ascValue: false, label: 'Created (New - Old)' },
  { key: 'updated_at_asc', value: 'updated_at', ascValue: true, label: 'Updated (Old - New)' },
  { key: 'updated_at_desc', value: 'updated_at', ascValue: false, label: 'Updated (New - Old)' },
];
const DEFAULT_SORT_OPTION_KEY = 'created_at_desc';

const PAGE_SIZE = 50;
const TOTAL_COUNT_KEY = 'num_of_items';

export default function OrganizationViewStates() {
  const api = useApiContext();
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const [viewStates, setViewStates] = useState<OrganizationViewStateMetadata[]>([]);
  const [queryStats, setQueryStats] = useState<Record<string, number>>({});
  const [searchParam, setSearchParam] = useState('');
  const [localSearchParam, setLocalSearchParam] = useState('');
  const [assetType, setAssetType] = useState('');
  const [sortOptionKey, setSortOptionKey] = useState(DEFAULT_SORT_OPTION_KEY);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Asset types that are present, ordered by name
  const assetTypes = Object.keys(queryStats)
    .filter((type) => type !== TOTAL_COUNT_KEY)
    .map((type) => ontology.assetTypes[type])
    .filter((type) => type !== undefined)
    .sort((left, right) => getAssetTypeName(left).localeCompare(getAssetTypeName(right)));

  const fetchViewStates = useCallback(async () => {
    setIsLoading(true);
    const sortOption = VIEW_STATE_SORT_OPTIONS.find((option) => option.key === sortOptionKey) || {
      value: 'created_at',
      ascValue: false,
    };
    const params: ViewStateAdminQueryParams = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      searchParam,
      assetType,
      orderBy: sortOption.value,
      asc: sortOption.ascValue,
    };
    try {
      const response = await api.viewState.listAllViewStatesForOrg(params);
      setViewStates(response.viewStates);
      setQueryStats(response.queryStats);
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : `Unable to load organization ${VIEW_STATE_TERMS.plural}.`;
      showErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [api, page, searchParam, assetType, sortOptionKey]);

  useEffect(() => {
    fetchViewStates();
  }, [fetchViewStates]);

  useEffect(() => {
    setLocalSearchParam(searchParam);
  }, [searchParam]);

  const totalItems = assetType ? queryStats[assetType] || 0 : queryStats[TOTAL_COUNT_KEY] || 0;
  const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const setSearchParamater = (value: string) => {
    setPage(0);
    setSearchParam(value);
  };
  const debouncedUpdate = debounceInComponent(setSearchParamater, 250);

  return (
    <section className={styles.viewStatesPanel}>
      <div className={styles.searchArea}>
        <input
          value={localSearchParam}
          onChange={(event) => {
            setLocalSearchParam(event.target.value);
            debouncedUpdate(event.target.value);
          }}
          placeholder="Search name, description, creator or asset name"
        />
        <div className={styles.assetTypeWrapper}>
          {assetTypes.map((type) => (
            <div
              key={type.identifier}
              className={!assetType || assetType === type.identifier ? styles.assetTypeTagActive : styles.assetTypeTag}
              onClick={() => {
                // Click to select or deselect the asset type filter
                let newFilter = '';
                if (assetType !== type.identifier) newFilter = type.identifier;
                setPage(0);
                setAssetType(newFilter);
              }}
            >
              {getAssetTypeName(type)}
            </div>
          ))}
        </div>
        <div className={styles.separator} />
      </div>
      <div className={styles.selectLabel}>
        Sort by:
        <select
          onChange={(sortOption) => {
            const newValue = sortOption.target.value;
            const selectedOption = VIEW_STATE_SORT_OPTIONS.find((option) => option.key === newValue);
            if (selectedOption) {
              setPage(0);
              setSortOptionKey(newValue);
            }
          }}
          value={sortOptionKey}
          disabled={isLoading}
        >
          {VIEW_STATE_SORT_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {isLoading && <Spinner />}
      {!isLoading && viewStates.length === 0 && (
        <div className={styles.emptyState}>{`No ${VIEW_STATE_TERMS.plural} found.`}</div>
      )}
      {!isLoading && viewStates.length > 0 && (
        <div className={styles.cardGrid}>
          {viewStates.map((viewState) => (
            <ViewStateCard key={viewState.id} viewState={viewState} onChanged={fetchViewStates} />
          ))}
        </div>
      )}
      {!isLoading && !!pageCount && pageCount > 1 && (
        <div className={styles.selectLabel}>
          Page:
          <select value={page} disabled={isLoading} onChange={(e) => setPage(Number(e.target.value))}>
            {Array.from({ length: pageCount }, (_, i) => i).map((option) => (
              <option key={option} value={option}>
                {option + 1}
              </option>
            ))}
          </select>
        </div>
      )}
    </section>
  );
}
